/**
 * Pure helpers for manipulating Tiptap flashcard marks on a JSONContent
 * tree — no editor instance required.
 *
 * Lives in sync because `card-notes-cleanup` needs to rewrite marks when
 * cards are soft-deleted/restored. The shape (`JSONContent`) comes from
 * sync-engine/schema so this file has zero tiptap dependency.
 *
 * Both helpers return the INPUT object itself when there is nothing to change
 * (structural sharing all the way down), so a caller can tell "no-op" apart
 * from "changed" by identity — and skip the write. That is what lets the
 * cleanup say "nothing to do" instead of rewriting a note that already had
 * no mark to remove.
 */

import type { JSONContent } from '@flashka/shared/sync-engine/schema/shared-types';

const MARK_NAME = 'flashcard';
const ID_ATTR = 'data-flashcard-id';

type Mark = NonNullable<JSONContent['marks']>[number];

const markId = (mark: Mark): string | undefined => {
  const id = mark.attrs?.[ID_ATTR];
  return mark.type === MARK_NAME && typeof id === 'string' ? id : undefined;
};

const hasMark = (node: JSONContent, id: string): boolean =>
  (node.marks ?? []).some((mark) => markId(mark) === id);

/** Map `content.content` through `f`, sharing structure when nothing changed. */
function mapChildren(
  content: JSONContent,
  f: (node: JSONContent, i: number) => JSONContent,
): JSONContent {
  if (!content.content) return content;
  let changed = false;
  const next = content.content.map((node, i) => {
    const out = f(node, i);
    if (out !== node) changed = true;
    return out;
  });
  return changed ? { ...content, content: next } : content;
}

/**
 * Remove flashcard marks for the given card ids from a JSONContent tree.
 * Returns a new tree, or the input itself when no mark was found — the
 * input is never mutated.
 */
export function removeFlashcardMarksFromJSON(
  content: JSONContent,
  cardIds: ReadonlySet<string>,
): JSONContent {
  return mapChildren(content, (node) => {
    if (node.content) {
      return removeFlashcardMarksFromJSON(node, cardIds);
    }

    if (!node.marks) return node;

    const filtered = node.marks.filter((mark) => {
      const id = markId(mark);
      return id === undefined || !cardIds.has(id);
    });

    if (filtered.length === node.marks.length) return node;

    const result = { ...node };
    if (filtered.length === 0) {
      delete result.marks;
    } else {
      result.marks = filtered;
    }
    return result;
  });
}

/** Every flashcard id a JSON tree's text marks carry. */
export function collectFlashcardMarkIdsFromJSON(content: JSONContent): Set<string> {
  const ids = new Set<string>();
  const visit = (node: JSONContent) => {
    for (const mark of node.marks ?? []) {
      const id = markId(mark);
      if (id !== undefined) ids.add(id);
    }
    for (const child of node.content ?? []) visit(child);
  };
  visit(content);
  return ids;
}

/** A block whose children are inline leaves (text, hard breaks, mentions…). */
const isTextBlock = (node: JSONContent): boolean =>
  !!node.content && node.content.some((child) => child.type === 'text');

/**
 * The text each wanted mark covered in the snapshot, per block: a run is the
 * text of consecutive leaves carrying the same id. What comes back is a
 * multiset of `{ id, text }` in document order.
 */
function collectMarkedRuns(
  snapshot: JSONContent,
  cardIds: ReadonlySet<string>,
): Array<{ id: string; text: string }> {
  const runs: Array<{ id: string; text: string }> = [];
  const visit = (node: JSONContent) => {
    if (!node.content) return;
    if (isTextBlock(node)) {
      const open = new Map<string, string>();
      const flush = (keep: ReadonlySet<string>) => {
        for (const [id, text] of open) {
          if (keep.has(id)) continue;
          if (text) runs.push({ id, text });
          open.delete(id);
        }
      };
      for (const leaf of node.content) {
        const ids = new Set(
          (leaf.marks ?? [])
            .map(markId)
            .filter((id): id is string => id !== undefined && cardIds.has(id)),
        );
        // A leaf without the id ends that id's run.
        flush(ids);
        if (leaf.type !== 'text' || !leaf.text) continue;
        for (const id of ids) open.set(id, (open.get(id) ?? '') + leaf.text);
      }
      flush(new Set());
      return;
    }
    for (const child of node.content) visit(child);
  };
  visit(snapshot);
  return runs;
}

/**
 * Mark `[from, to)` of a block's plain text with `mark`, splitting the text
 * leaves it crosses. Leaves that already carry the id are left as they are.
 */
function markBlockRange(
  block: JSONContent,
  from: number,
  to: number,
  id: string,
  mark: Mark,
): JSONContent {
  let offset = 0;
  const next: JSONContent[] = [];
  let changed = false;
  for (const leaf of block.content ?? []) {
    if (leaf.type !== 'text' || !leaf.text) {
      next.push(leaf);
      continue;
    }
    const start = offset;
    const end = offset + leaf.text.length;
    offset = end;
    const a = Math.max(start, from);
    const b = Math.min(end, to);
    if (a >= b || hasMark(leaf, id)) {
      next.push(leaf);
      continue;
    }
    changed = true;
    const marked = (text: string): JSONContent => ({
      ...leaf,
      text,
      marks: [...(leaf.marks ?? []), mark],
    });
    if (a > start) next.push({ ...leaf, text: leaf.text.slice(0, a - start) });
    next.push(marked(leaf.text.slice(a - start, b - start)));
    if (b < end) next.push({ ...leaf, text: leaf.text.slice(b - start) });
  }
  return changed ? { ...block, content: next } : block;
}

/**
 * Where, in a block's plain text, `text` occurs at a place not already marked
 * with `id`. `-1` when nowhere.
 */
function findUnmarkedRun(block: JSONContent, text: string, id: string): number {
  let plain = '';
  const covered: Array<[number, number]> = [];
  for (const leaf of block.content ?? []) {
    if (leaf.type !== 'text' || !leaf.text) continue;
    if (hasMark(leaf, id)) covered.push([plain.length, plain.length + leaf.text.length]);
    plain += leaf.text;
  }
  let at = plain.indexOf(text);
  while (at !== -1) {
    const end = at + text.length;
    const overlaps = covered.some(([a, b]) => a < end && at < b);
    if (!overlaps) return at;
    at = plain.indexOf(text, at + 1);
  }
  return -1;
}

/**
 * Re-apply flashcard marks from a saved document snapshot into the current
 * tree, BY CONTENT: for each wanted id, the text its highlight covered in the
 * snapshot is looked up in the current document's blocks, and marked where it
 * is found — first match wins, one match per run. A run whose text is no
 * longer there (the user rewrote the sentence) is simply not restored.
 *
 * Content, not position, because the current document may have moved on
 * since the snapshot — a paragraph added on another device, the text leaves
 * merged by the editor once the mark came out — and a parallel walk would
 * give up at the first difference. Returns a new tree, or the input `current`
 * itself when nothing was restored. Never copies TEXT from the snapshot: the
 * snapshot only says WHAT was highlighted.
 */
export function addFlashcardMarksFromSnapshot(
  current: JSONContent,
  snapshot: JSONContent,
  cardIds: ReadonlySet<string>,
): JSONContent {
  const pending = collectMarkedRuns(snapshot, cardIds);
  if (pending.length === 0) return current;

  const visit = (node: JSONContent): JSONContent => {
    if (!node.content || pending.length === 0) return node;
    if (!isTextBlock(node)) return mapChildren(node, visit);

    let block = node;
    for (let i = 0; i < pending.length; ) {
      const run = pending[i];
      const at = findUnmarkedRun(block, run.text, run.id);
      if (at === -1) {
        i += 1;
        continue;
      }
      block = markBlockRange(block, at, at + run.text.length, run.id, {
        type: MARK_NAME,
        attrs: { [ID_ATTR]: run.id },
      });
      pending.splice(i, 1);
    }
    return block;
  };

  return visit(current);
}

/** Concatenated text of a JSONContent node (recursive over text leaves). */
function nodeText(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(nodeText).join('');
}

function nodeHasFlashcardMark(node: JSONContent, cardId: string): boolean {
  return !!node.marks?.some(
    (mark) => mark.type === 'flashcard' && mark.attrs?.['data-flashcard-id'] === cardId,
  );
}

/** Add the card's flashcard mark to every text leaf under `node`. */
function markAllTextLeaves(node: JSONContent, cardId: string): JSONContent {
  if (node.type === 'text') {
    if (nodeHasFlashcardMark(node, cardId)) return node;
    return {
      ...node,
      marks: [
        ...(node.marks ?? []),
        { type: 'flashcard', attrs: { 'data-flashcard-id': cardId } },
      ],
    };
  }
  if (!node.content) return node;
  return {
    ...node,
    content: node.content.map((child) => markAllTextLeaves(child, cardId)),
  };
}

/**
 * Apply a flashcard mark to every block whose trimmed text matches one of
 * `paragraphTexts` (`exact`), or contains the single search text
 * (`contains`). Editor-agnostic — the fallback used when flashcards are
 * generated while no notes editor is mounted, so the marks can still be
 * written to the notes document via `writeNotesContent` instead of being
 * silently dropped. Prefers the innermost matching block (a matching
 * paragraph wins over its list/blockquote ancestor). Returns a new tree
 * plus how many blocks were marked.
 */
export function addFlashcardMarkByParagraphText(
  content: JSONContent,
  paragraphTexts: string[],
  cardId: string,
  match: 'exact' | 'contains' = 'exact',
): { content: JSONContent; markedCount: number } {
  const targets = paragraphTexts.map((t) => t.trim()).filter((t) => t.length > 0);
  if (targets.length === 0) return { content, markedCount: 0 };
  const exactTargets = new Set(targets);

  let markedCount = 0;

  const matches = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return false;
    if (match === 'exact') return exactTargets.has(trimmed);
    return targets.some((t) => trimmed.includes(t));
  };

  /** Returns the (possibly) rewritten node and whether anything inside it
   *  (or the node itself) received the mark. */
  const walk = (node: JSONContent): { node: JSONContent; marked: boolean } => {
    if (node.type === 'text' || !node.content || node.content.length === 0) {
      return { node, marked: false };
    }

    let anyChildMarked = false;
    const children = node.content.map((child) => {
      const result = walk(child);
      if (result.marked) anyChildMarked = true;
      return result.node;
    });

    if (anyChildMarked) {
      return { node: { ...node, content: children }, marked: true };
    }

    if (matches(nodeText(node))) {
      markedCount++;
      return { node: markAllTextLeaves(node, cardId), marked: true };
    }

    return { node, marked: false };
  };

  // Walk the root's children (never mark the doc node itself).
  if (!content.content) return { content, markedCount: 0 };
  let anyMarked = false;
  const rewritten = content.content.map((child) => {
    const result = walk(child);
    if (result.marked) anyMarked = true;
    return result.node;
  });

  return {
    content: anyMarked ? { ...content, content: rewritten } : content,
    markedCount,
  };
}
